import React, { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import PageLayout, {
  PageSection,
} from "../../components/navigation/PageLayout";
import Icon from "../../components/AppIcon";
import RuleTypeSelector, { STRATEGY_DESCRIPTIONS } from "../../components/distribution/RuleTypeSelector";
import RuleActivationModal from "../../components/distribution/RuleActivationModal";
import UniversalOptionsPanel from "../../components/distribution/UniversalOptionsPanel";
import SeniorityBandsModal from "../../components/distribution/SeniorityBandsModal";
import {
    useDistributionRules,
  useDistributionRuleMutations,
} from "../../hooks/useDistributionRules";
import { useJobRoles } from "../../hooks/useJobRoles";
import { useTenantContext } from "../../contexts/TenantContext";
import { distributionRulesApi } from "../../services/api/distributionRules";
import { seniorityBandsApi } from "../../services/api/seniorityBands";

// Default departments (used as fallback)
const DEFAULT_DEPARTMENTS = [
  { id: "FOH", name: "Front of House (FOH)" },
    { id: "BOH", name: "Back of House (BOH)" },
  { id: "Admin", name: "Administration" },
    { id: "Management", name: "Management" },
];

// Simplified distribution type mappings (3 core types)
const TYPE_MAP = {
    Equal: 0,
    RoleScore: 1,
    ByDepartment: 2,
    // Legacy mappings
    ByJobRole: 1,
    ByJobRoleScore: 1,
};

// Reverse mapping
const TYPE_NAMES = {
 0: "Equal Split",
    1: "Role Score",
    2: "By Department",
};

// Types that require manual percentage allocation (only By Department now)
const TYPES_REQUIRING_ALLOCATION = [2]; // ByDepartment

const RuleManager = () => {
    const navigate = useNavigate();
    const [selectedRule, setSelectedRule] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
 const [notification, setNotification] = useState(null);
    const [loadingAllocations, setLoadingAllocations] = useState(false);
    const [showActivationModal, setShowActivationModal] = useState(false);
    const [ruleToActivate, setRuleToActivate] = useState(null);
    const [showSeniorityModal, setShowSeniorityModal] = useState(false);
    const [tenantSeniorityBands, setTenantSeniorityBands] = useState([]);
    const [loadingTenantBands, setLoadingTenantBands] = useState(false);

  // Tenant context
    const { selectedTenant, needsTenantSelection, isGlobalAdmin, userRole } =
 useTenantContext();

    // Only fetch data when tenant is selected (or for non-global admins)
    const shouldFetchData = !isGlobalAdmin || selectedTenant;

    // Check if user can activate rules
    const canActivateRules =
        userRole === "TenantAdmin" || userRole === "GlobalAdmin";

    // Check if user can create/edit/delete rules
    // GlobalAdmins CAN modify rules when they have selected a tenant
    const canModifyRules = userRole === "TenantAdmin" || userRole === "Manager" || (userRole === "GlobalAdmin" && selectedTenant);

    // Form state
const [formData, setFormData] = useState({
        name: "",
        description: "",
        type: 0,
        isActive: false,
        priority: 100,
        allocations: [],
        isDraft: true,
        effectiveFrom: null,
        // Universal options
    enableMinimumFloor: false,
        minimumFloorAmount: 0,
        enableHourFactor: false,
    hourFactorWeight: 0.5,
        enableSeniorityFactor: false,
        seniorityFactorWeight: 0.3,
        seniorityBandsJson: null,
    });

    // Fetch data
    const { rules, loading, error, refetch } = useDistributionRules({
      autoFetch: shouldFetchData,
    });
    const {
        createRule,
        updateRule,
        deleteRule,
        activateRule,
        deactivateRule,
        loading: mutationLoading,
    } = useDistributionRuleMutations();
    const {
        jobRoles,
        loading: jobRolesLoading,
        refetch: refetchJobRoles,
    } = useJobRoles({
  autoFetch: shouldFetchData,
        activeOnly: true,
    });

    const tenantJobRoles = jobRoles || [];

    // Get distinct departments from job roles
 const departments = useMemo(() => {
    if (!tenantJobRoles || tenantJobRoles.length === 0) {
        return DEFAULT_DEPARTMENTS;
        }
  
        // Get distinct departments from job roles
   const distinctDepts = [...new Set(
  tenantJobRoles
   .map(role => role.department)
 .filter(dept => dept && dept.trim() !== '')
        )];
        
        if (distinctDepts.length === 0) {
 return DEFAULT_DEPARTMENTS;
   }
        
        // Map to department objects with friendly names
        const deptNameMap = {
  'FOH': 'Front of House (FOH)',
            'BOH': 'Back of House (BOH)',
      'Admin': 'Administration',
    'Management': 'Management',
    'Kitchen': 'Kitchen (BOH)',
 'Bar': 'Bar (FOH)',
     'Waitstaff': 'Waitstaff (FOH)',
  };
        
        return distinctDepts.map(dept => ({
    id: dept,
  name: deptNameMap[dept] || dept
   })).sort((a, b) => a.name.localeCompare(b.name));
    }, [tenantJobRoles]);

    // Refetch data when tenant changes
    useEffect(() => {
  if (shouldFetchData) {
      refetch();
       refetchJobRoles();
        loadTenantSeniorityBands();
     setSelectedRule(null);
  setIsEditing(false);
   setIsCreating(false);
        }
  }, [selectedTenant?.id, shouldFetchData]);

    // Load tenant-level seniority bands
    const loadTenantSeniorityBands = async () => {
 if (!shouldFetchData) return;
  
        setLoadingTenantBands(true);
        try {
            const response = await seniorityBandsApi.get();
            if (response.success && response.data?.bands) {
                setTenantSeniorityBands(response.data.bands);
   }
        } catch (err) {
  console.error("Failed to load tenant seniority bands:", err);
   } finally {
        setLoadingTenantBands(false);
        }
    };

    const breadcrumbs = [
    { label: "Dashboard", path: "/dashboard-overview" },
        { label: "Settings", path: "/settings/distribution-rules" },
        { label: "Distribution Rules", path: "/settings/distribution-rules" },
    ];

    const showNotification = (message, type = "success") => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 5000);
    };

    // Calculate total percentage for types requiring manual allocation
    const totalPercentage = formData.allocations.reduce(
    (sum, a) => sum + (parseFloat(a.percentage) || 0),
        0,
    );
  const requiresManualAllocation = TYPES_REQUIRING_ALLOCATION.includes(formData.type);
    const isValidPercentage =
  !requiresManualAllocation || Math.abs(totalPercentage - 100) < 0.01;

    // Get distribution type display name
    const getTypeDisplay = (type) => {
        const numType = parseRuleType(type);
   return TYPE_NAMES[numType] || "Unknown";
    };

    // Parse rule type from string or number - handles legacy mappings
    const parseRuleType = (type) => {
        if (typeof type === "number") {
            // New types: Equal=0, RoleScore=1, ByDepartment=2
            // Legacy types: Equal=0, ByJobRole=1, ByJobRoleScore=2, ByDepartment=3
        if (type === 3) return 2; // Old ByDepartment -> new ByDepartment
            if (type > 3) return 0;   // Any other legacy type -> Equal
          return type; // 0, 1, 2 stay as-is
        }
        return TYPE_MAP[type] ?? 0;
    };

    const handleCreateNew = () => {
        setFormData({
      name: "",
description: "",
            type: 0,
            isActive: false,
            priority: 100,
   allocations: [],
            isDraft: true,
   effectiveFrom: null,
    enableMinimumFloor: false,
 minimumFloorAmount: 0,
            enableHourFactor: false,
            hourFactorWeight: 0.5,
 enableSeniorityFactor: false,
         seniorityFactorWeight: 0.3,
            seniorityBandsJson: null,
        });
    setSelectedRule(null);
     setIsCreating(true);
        setIsEditing(true);
    };

    const handleEditRule = async (rule) => {
      console.log("Editing rule:", rule);

        // Convert type to number using the unified parser
   const ruleType = parseRuleType(rule.type);

    // Set initial form data from the list DTO (will be updated from detail below)
 setFormData({
   name: rule.name,
   description: rule.description || "",
 type: ruleType,
            isActive: rule.isActive,
        priority: rule.priority,
            allocations: [],
     isDraft: rule.isDraft || false,
 effectiveFrom: null,
            // Universal options - defaults, will be overridden from detail API
            enableMinimumFloor: rule.enableMinimumFloor || false,
            minimumFloorAmount: 0,
            enableHourFactor: rule.enableHourFactor || false,
  hourFactorWeight: 0.5,
          enableSeniorityFactor: rule.enableSeniorityFactor || false,
            seniorityFactorWeight: 0.3,
      seniorityBandsJson: null,
    });

      setSelectedRule(rule);
  setIsCreating(false);
    setIsEditing(true);

        // Always load full detail from API to get universal options and allocations
        setLoadingAllocations(true);
    try {
            const response = await distributionRulesApi.getById(rule.id);
            if (response.success && response.data) {
          const detail = response.data;

           // Check if rule has an effective from date
       const hasEffectiveDate = detail.effectiveFrom != null && !detail.isDraft;
    const effectiveDate = hasEffectiveDate
          ? new Date(detail.effectiveFrom).toISOString().split("T")[0]
            : null;

              // Parse seniority bands if present
   let seniorityBands = [];
  if (detail.seniorityBandsJson) {
    try {
    seniorityBands = JSON.parse(detail.seniorityBandsJson);
  } catch (e) {
      console.error("Failed to parse seniority bands:", e);
         }
           } else if (detail.seniorityBands) {
                    seniorityBands = detail.seniorityBands;
       }

                setFormData((prev) => ({
        ...prev,
       name: detail.name,
    description: detail.description || "",
         type: parseRuleType(detail.type),
  isActive: detail.isActive,
         priority: detail.priority,
   isDraft: detail.isDraft || false,
  effectiveFrom: effectiveDate,
         // Universal options from detail response
        enableMinimumFloor: detail.enableMinimumFloor || false,
        minimumFloorAmount: detail.minimumFloorAmount || 0,
         enableHourFactor: detail.enableHourFactor || false,
           hourFactorWeight: detail.hourFactorWeight ?? 0.5,
        enableSeniorityFactor: detail.enableSeniorityFactor || false,
         seniorityFactorWeight: detail.seniorityFactorWeight ?? 0.3,
  seniorityBandsJson: detail.seniorityBandsJson || null,
              seniorityBands: seniorityBands,
            // Allocations from detail response
     allocations: (detail.allocations || []).map((a) => ({
         jobRoleId: a.jobRoleId,
  department: a.department,
        percentage: parseFloat(a.percentage) || 0,
            sortOrder: a.sortOrder || 0,
         })),
                }));
            }
 } catch (err) {
            console.error("Failed to load rule details:", err);
            showNotification("Failed to load rule details", "error");
        } finally {
       setLoadingAllocations(false);
    }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
      setIsCreating(false);
        setSelectedRule(null);
        setFormData({
     name: "",
            description: "",
         type: 0,
        isActive: false,
            priority: 100,
       allocations: [],
        isDraft: true,
  effectiveFrom: null,
      enableMinimumFloor: false,
    minimumFloorAmount: 0,
   enableHourFactor: false,
        hourFactorWeight: 0.5,
            enableSeniorityFactor: false,
          seniorityFactorWeight: 0.3,
            seniorityBandsJson: null,
        });
    };

    const handleSimulateRule = () => {
        if (selectedRule?.id) {
      navigate(`/distribution-lab?ruleId=${selectedRule.id}`);
        } else if (isCreating && formData.name) {
            showNotification(
   "Please save the rule first before simulating it in the Lab.",
    "warning",
    );
   } else {
   navigate("/distribution-lab");
        }
};

    const handleSave = async () => {
        if (!formData.name.trim()) {
            showNotification("Rule name is required", "error");
  return;
        }

        if (requiresManualAllocation && !isValidPercentage) {
   showNotification("Allocations must sum to exactly 100%", "error");
        return;
 }

    // Validate effective date if NOT a draft
        if (!formData.isDraft) {
            if (!formData.effectiveFrom) {
          showNotification(
          "Please select an effective from date for non-draft rules",
               "error",
     );
      return;
            }

         const selectedDate = new Date(formData.effectiveFrom);
            const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
   tomorrow.setHours(0, 0, 0, 0);
            selectedDate.setHours(0, 0, 0, 0);

 if (selectedDate < tomorrow) {
   showNotification(
       "Effective date must be at least tomorrow",
              "error",
      );
 return;
   }
      }

        try {
  const ruleData = {
                name: formData.name,
              description: formData.description || null,
        type: formData.type,
         priority: formData.priority,
        isDraft: formData.isDraft,
          effectiveFrom:
     !formData.isDraft && formData.effectiveFrom
            ? new Date(formData.effectiveFrom).toISOString()
: null,
      effectiveTo: null,
          // Universal options
   enableMinimumFloor: formData.enableMinimumFloor,
      minimumFloorAmount: formData.enableMinimumFloor ? formData.minimumFloorAmount : null,
      enableHourFactor: formData.enableHourFactor,
         hourFactorWeight: formData.hourFactorWeight,
         enableSeniorityFactor: formData.enableSeniorityFactor,
       seniorityFactorWeight: formData.seniorityFactorWeight,
       // Send seniorityBands as an array (backend expects List<SeniorityBandDto>, not a JSON string)
   seniorityBands: formData.seniorityBands && formData.seniorityBands.length > 0
        ? formData.seniorityBands
           : formData.seniorityBandsJson
 ? (() => { try { return JSON.parse(formData.seniorityBandsJson); } catch { return null; } })()
             : null,
      // Allocations only for ByDepartment
  allocations: requiresManualAllocation
           ? formData.allocations.map((a, idx) => ({
          department: a.department,
    percentage: parseFloat(a.percentage) || 0,
         sortOrder: idx,
    }))
       : [],
            };

     if (isCreating) {
    await createRule(ruleData);
      showNotification(
                formData.isDraft
                 ? "Draft rule created. Use the Lab page to test this rule."
             : `Rule created and will become active on ${new Date(formData.effectiveFrom).toLocaleDateString()}`,
         "success",
                );
            } else {
       await updateRule(selectedRule.id, {
          ...ruleData,
     updateReason: "Updated via Rule Manager",
      });
   showNotification(
           formData.isDraft
  ? "Draft rule updated successfully"
                 : "Rule updated successfully",
     );
    }

      refetch();
     handleCancelEdit();
        } catch (err) {
      showNotification(err.message || "Failed to save rule", "error");
        }
    };

    const handleDeleteRule = async (rule) => {
  if (rule.hasBeenDistributed) {
showNotification(
      "Cannot delete a rule that has been used in distributions. You can deactivate it instead.",
     "error",
       );
            return;
        }

        if (rule.isActive) {
 showNotification(
                "Cannot delete an active rule. Deactivate it first.",
       "error",
            );
 return;
     }

if (!confirm(`Are you sure you want to delete "${rule.name}"?`)) return;

        try {
       await deleteRule(rule.id);
showNotification("Rule deleted successfully");
    refetch();
        } catch (err) {
    showNotification(err.message || "Failed to delete rule", "error");
      }
    };

  const handleActivateClick = (rule) => {
   if (!canActivateRules) {
     showNotification(
    "Only Tenant Administrators can activate distribution rules.",
          "error",
      );
    return;
  }

   if (rule.isDraft) {
         showNotification(
         "Cannot activate a draft rule. Edit the rule and uncheck 'Draft Rule' first.",
         "error",
            );
            return;
        }

   setRuleToActivate(rule);
      setShowActivationModal(true);
    };

    const handleActivateConfirm = async (effectiveDate) => {
        try {
            await activateRule(ruleToActivate.id, effectiveDate);
       showNotification(
     `Rule will be activated on ${effectiveDate.toLocaleDateString()}`,
    "success",
        );
          setShowActivationModal(false);
            setRuleToActivate(null);
     refetch();
        } catch (err) {
            showNotification(err.message || "Failed to activate rule", "error");
  }
    };

    const handleDeactivate = async (rule) => {
 if (!confirm(`Are you sure you want to deactivate "${rule.name}"?`))
     return;

        try {
            await deactivateRule(rule.id, "Deactivated via Rule Manager");
       showNotification("Rule deactivated successfully");
          refetch();
  } catch (err) {
  showNotification(
  err.message || "Failed to deactivate rule",
    "error",
         );
        }
    };

    const handleTypeChange = (type) => {
    // Clear allocations if switching away from ByDepartment
        const newAllocations = TYPES_REQUIRING_ALLOCATION.includes(type)
       ? formData.allocations
       : [];

        setFormData((prev) => ({
   ...prev,
       type,
            allocations: newAllocations,
      }));
    };

    const handleUniversalOptionsChange = (options) => {
        setFormData((prev) => ({
    ...prev,
 ...options,
        }));
    };

    const handleSeniorityBandsSave = async (bands) => {
        try {
   const response = await seniorityBandsApi.save(bands);
  if (response.success) {
           setTenantSeniorityBands(bands);
     showNotification("Seniority bands saved successfully", "success");
  } else {
  showNotification(response.message || "Failed to save seniority bands", "error");
            }
        } catch (err) {
         showNotification(err.message || "Failed to save seniority bands", "error");
        }
        setShowSeniorityModal(false);
    };

    const handleAddAllocation = () => {
        // Only ByDepartment (type 2) needs allocations now
        if (formData.type === 2) {
       const usedDepts = formData.allocations.map((a) => a.department);
  const availableDept = departments.find(
            (d) => !usedDepts.includes(d.id),
     );
   if (!availableDept) {
    showNotification("All departments have been added", "warning");
       return;
     }
      setFormData((prev) => ({
        ...prev,
    allocations: [
    ...prev.allocations,
  {
             department: availableDept.id,
         percentage: 0,
          sortOrder: prev.allocations.length,
            },
      ],
      }));
        }
    };

    const handleRemoveAllocation = (index) => {
     setFormData((prev) => ({
  ...prev,
      allocations: prev.allocations.filter((_, i) => i !== index),
    }));
    };

    const handleAllocationChange = (index, field, value) => {
        setFormData((prev) => ({
     ...prev,
  allocations: prev.allocations.map((a, i) =>
  i === index ? { ...a, [field]: value } : a,
 ),
        }));
    };

    const distributeEqually = () => {
        if (formData.allocations.length === 0) return;
        const equalShare =
       Math.floor((100 / formData.allocations.length) * 100) / 100;
const remainder = 100 - equalShare * formData.allocations.length;

   setFormData((prev) => ({
        ...prev,
 allocations: prev.allocations.map((a, i) => ({
 ...a,
              percentage: i === 0 ? equalShare + remainder : equalShare,
     })),
        }));
    };

    // Select Tenant Empty State for Global Admins
  if (isGlobalAdmin && needsTenantSelection) {
     return (
        <>
        <Helmet>
    <title>Distribution Rules - Rocket Tronc</title>
   </Helmet>
                <PageLayout
         title="Distribution Rules"
             description="Configure TRONC distribution rules."
         breadcrumbs={breadcrumbs}
     >
  <div className="card p-12 text-center">
       <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
       <Icon name="Building2" size={40} className="text-primary" />
         </div>
       <h2 className="text-2xl font-bold mb-3">Select a Tenant</h2>
     <p className="text-muted-foreground mb-6 max-w-md mx-auto">
    As a Global Administrator, please select a tenant from the sidebar
        to view and manage their distribution rules.
               </p>
  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Icon name="ArrowLeft" size={16} />
        <span>Use the tenant selector in the sidebar</span>
        </div>
        </div>
            </PageLayout>
            </>
        );
    }

    return (
        <>
            <Helmet>
           <title>
  Distribution Rules - {selectedTenant?.name || "Rocket Tronc"}
                </title>
   <meta
       name="description"
      content="Configure TRONC distribution rules and policy compliance"
         />
            </Helmet>

         <PageLayout
                title="Distribution Rules"
           description={
         selectedTenant
        ? `Viewing ${selectedTenant.name} - Configure TRONC distribution rules.`
     : "Configure TRONC distribution rules. Rules define how tips are distributed among eligible employees."
     }
      breadcrumbs={breadcrumbs}
    >
      {/* Notification Toast */}
         {notification && (
    <div
    className={`fixed top-20 right-4 z-[1300] alert ${
          notification.type === "error"
              ? "alert-error"
 : notification.type === "warning"
       ? "alert-warning"
    : "alert-success"
           } shadow-lg`}
     >
          <Icon
          name={notification.type === "error" ? "AlertCircle" : "CheckCircle"}
   size={20}
       />
           <span>{notification.message}</span>
        <button onClick={() => setNotification(null)} className="ml-2">
            <Icon name="X" size={16} />
     </button>
        </div>
       )}

    {/* Action Buttons */}
   <div className="flex flex-wrap gap-3 mb-6">
    {canModifyRules ? (
   <button className="btn btn-primary" onClick={handleCreateNew}>
    <Icon name="Plus" size={18} />
Create New Rule
   </button>
    ) : (
      <div className="p-3 bg-warning/10 border border-warning/20 rounded-lg text-sm text-warning flex items-center gap-2">
    <Icon name="Lock" size={16} />
            <span>Global Admins cannot create or modify distribution rules</span>
        </div>
    )}
    <a href="/distribution-lab" className="btn btn-outline">
   <Icon name="FlaskConical" size={18} />
       Test in Lab
      </a>
     {canModifyRules && (
         <button 
className="btn btn-outline"
   onClick={() => setShowSeniorityModal(true)}
         >
      <Icon name="Award" size={18} />
      Seniority Bands
</button>
    )}
 </div>

      {/* Loading State */}
        {loading && (
 <div className="card p-8 text-center">
   <div className="spinner mx-auto mb-4" />
       <p className="text-muted-foreground">Loading distribution rules...</p>
           </div>
      )}

         {/* Error State */}
      {error && !loading && (
             <div className="alert alert-error mb-6">
          <Icon name="AlertCircle" size={20} />
            <span>{error}</span>
 <button className="btn btn-outline btn-sm ml-auto" onClick={refetch}>
  <Icon name="RefreshCw" size={16} />
                 Retry
     </button>
               </div>
       )}

       {/* Main Content */}
          {!loading && !error && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
           {/* Rules List */}
         <div className="lg:col-span-1">
           <PageSection title="Distribution Rules">
            {rules.length === 0 ? (
       <div className="card p-8 text-center">
  <Icon
           name="Settings"
         size={32}
      className="mx-auto mb-4 text-muted-foreground"
      />
  <p className="text-muted-foreground mb-4">
     No distribution rules configured
          </p>
        <button
     className="btn btn-primary btn-sm"
         onClick={handleCreateNew}
     >
    Create First Rule
  </button>
 </div>
         ) : (
   <div className="space-y-3">
         {rules.map((rule) => (
          <div
     key={rule.id}
            className={`card p-4 cursor-pointer transition-all hover:shadow-md ${
 selectedRule?.id === rule.id ? "ring-2 ring-primary" : ""
               }`}
      onClick={() => handleEditRule(rule)}
      >
       <div className="flex items-start justify-between">
            <div className="flex-1">
<div className="flex items-center gap-2 flex-wrap">
        <h4 className="font-semibold">{rule.name}</h4>
   {rule.isDraft && (
             <span className="badge badge-warning text-xs">Draft</span>
           )}
     {rule.isActive && !rule.isDraft && (
                     <span className="badge badge-success text-xs">Active</span>
    )}
             {rule.isScheduledForActivation && !rule.isDraft && (
          <span className="badge badge-info text-xs">Scheduled</span>
            )}
       {rule.hasBeenDistributed && (
      <span className="badge badge-neutral text-xs">Used</span>
 )}
            </div>
               <p className="text-sm text-muted-foreground mt-1">
   {getTypeDisplay(rule.type)}
          </p>
          {rule.effectiveFrom && !rule.isDraft && (
       <p className="text-xs text-muted-foreground mt-1">
             Effective: {new Date(rule.effectiveFrom).toLocaleDateString()}
      </p>
            )}
   </div>
               <div className="flex items-center gap-1">
               {(rule.isDraft || (!rule.hasBeenDistributed && !rule.isActive)) && (
       <button
      className="p-2 hover:bg-error/10 rounded-md"
            onClick={(e) => {
 e.stopPropagation();
          handleDeleteRule(rule);
            }}
         title="Delete rule"
            >
 <Icon name="Trash2" size={16} className="text-error" />
          </button>
      )}
    </div>
           </div>
     </div>
           ))}
        </div>
    )}
             </PageSection>
             </div>

        {/* Rule Editor */}
          <div className="lg:col-span-2">
       {isEditing ? (
             <PageSection title={isCreating ? "Create New Rule" : "Edit Rule"}>
         <div className="card">
             <div className="card-content space-y-6">
                    {/* Distributed Rule Warning */}
  {selectedRule?.hasBeenDistributed && (
  <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
           <div className="flex items-start gap-3">
       <Icon name="AlertTriangle" size={20} className="text-warning shrink-0 mt-0.5" />
   <div className="text-sm">
    <p className="font-medium mb-1">This rule has been used in distributions</p>
           <p className="text-muted-foreground">
        This rule cannot be modified. Create a new rule with a future effective date instead.
              </p>
    </div>
             </div>
     </div>
       )}

      {/* Basic Info */}
       <div className="grid grid-cols-1 gap-4">
        <div>
 <label className="label">Rule Name *</label>
        <input
       type="text"
           value={formData.name}
    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
    className="input"
                placeholder="e.g., Default Distribution"
          disabled={selectedRule?.hasBeenDistributed}
       />
     </div>
        </div>

           <div>
          <label className="label">Description</label>
         <textarea
       value={formData.description}
 onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
         className="input"
        rows={2}
        placeholder="Optional description..."
               disabled={selectedRule?.hasBeenDistributed}
    />
     </div>

      {/* Draft Rule Control */}
        {!selectedRule?.hasBeenDistributed && (
         <div className="bg-info/10 border border-info/20 rounded-lg p-4">
 <div className="space-y-3">
           <div className="flex items-start gap-3">
          <Icon name="FlaskConical" size={20} className="text-info shrink-0 mt-0.5" />
              <div className="flex-1">
          <h4 className="font-medium mb-2">Rule Status</h4>
         <p className="text-sm text-muted-foreground mb-3">
    Draft rules can be tested in the Lab but won't be used for actual distributions.
                   </p>

   <div className="space-y-3">
            {selectedRule?.isActive ? (
       <div className="bg-warning/10 border border-warning/20 rounded-lg p-3">
     <div className="flex items-start gap-2">
        <Icon name="AlertTriangle" size={16} className="text-warning shrink-0 mt-0.5" />
      <div>
           <p className="text-sm font-medium text-warning">Active Rule Cannot Be Converted to Draft</p>
 <p className="text-xs text-muted-foreground mt-1">
       This rule is currently active. To make changes, create a new rule with a future effective date.
           </p>
     </div>
        </div>
                </div>
            ) : (
      <>
               <label className="flex items-center gap-2 cursor-pointer">
          <input
        type="checkbox"
            checked={formData.isDraft}
   onChange={(e) =>
       setFormData((prev) => ({
    ...prev,
     isDraft: e.target.checked,
        effectiveFrom: e.target.checked
   ? null
      : (() => {
 const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
   return tomorrow.toISOString().split("T")[0];
           })(),
             }))
  }
        className="checkbox"
                 />
            <span className="font-medium">Draft Rule</span>
            <span className="text-xs text-muted-foreground">(for testing only)</span>
                </label>
           {!formData.isDraft && (
          <div className="pl-6 border-l-2 border-primary/20">
             <label className="label text-sm">Effective From Date *</label>
               <input
   type="date"
     value={formData.effectiveFrom || ""}
          onChange={(e) => setFormData((prev) => ({ ...prev, effectiveFrom: e.target.value }))}
   min={(() => {
             const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
     return tomorrow.toISOString().split("T")[0];
                })()}
           className="input w-full"
          />
    <p className="text-xs text-muted-foreground mt-1">
                Must be at least tomorrow. This rule will become active at midnight on this date.
      </p>
         </div>
)}
     </>
   )}
       </div>
         </div>
     </div>
     </div>
     </div>
             )}

         {/* Distribution Strategy Type Selection */}
     <div>
   <label className="label mb-3">Distribution Strategy *</label>
         <RuleTypeSelector
           selectedType={formData.type}
        onSelect={!selectedRule?.hasBeenDistributed ? handleTypeChange : null}
               readonly={selectedRule?.hasBeenDistributed}
             showDescriptions={true}
        />
     </div>

       {/* Department Allocation Configuration for ByDepartment */}
            {requiresManualAllocation && (
         <div>
          <div className="flex items-center justify-between mb-3">
       <label className="label">Department Allocations *</label>
 <div className="flex items-center gap-2">
  <span className={`text-sm font-medium ${isValidPercentage ? "text-success" : "text-error"}`}>
           Total: {totalPercentage.toFixed(2)}%
       </span>
   {!isValidPercentage && (
         <Icon name="AlertCircle" size={16} className="text-error" />
         )}
            </div>
      </div>

        {loadingAllocations ? (
             <div className="p-4 text-center">
<div className="spinner mx-auto mb-2" />
    <p className="text-sm text-muted-foreground">Loading allocations...</p>
    </div>
        ) : (
<div className="space-y-3">
        {formData.allocations.map((allocation, index) => (
  <div
        key={index}
     className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
          >
           <select
                  value={allocation.department || ""}
             onChange={(e) => handleAllocationChange(index, "department", e.target.value)}
               className="input flex-1"
            disabled={selectedRule?.hasBeenDistributed}
 >
        <option value="">Select department...</option>
     {departments.map((dept) => (
      <option key={dept.id} value={dept.id}>
       {dept.name}
      </option>
      ))}
    </select>

          <div className="flex items-center gap-2">
             <input
                   type="number"
          value={allocation.percentage}
      onChange={(e) =>
    handleAllocationChange(index, "percentage", parseFloat(e.target.value) || 0)
       }
             className="input w-24 text-center"
  min="0"
       max="100"
       step="0.01"
         disabled={selectedRule?.hasBeenDistributed}
     />
          <span className="text-muted-foreground">%</span>
               </div>

    {!selectedRule?.hasBeenDistributed && (
   <button
      type="button"
        onClick={() => handleRemoveAllocation(index)}
         className="p-2 hover:bg-error/10 rounded-md text-error"
     >
         <Icon name="Trash2" size={16} />
          </button>
        )}
     </div>
   ))}

     {!selectedRule?.hasBeenDistributed && (
          <div className="flex gap-2">
                     <button
       type="button"
    onClick={handleAddAllocation}
                className="btn btn-outline btn-sm"
  >
   <Icon name="Plus" size={16} />
   Add Department
  </button>
             {formData.allocations.length > 0 && (
    <button
    type="button"
   onClick={distributeEqually}
          className="btn btn-outline btn-sm"
      >
      <Icon name="Equal" size={16} />
         Distribute Equally
   </button>
        )}
          </div>
          )}
               </div>
       )}

           {!isValidPercentage && (
         <p className="text-sm text-error mt-2">
          Allocations must sum to exactly 100% (currently {totalPercentage.toFixed(2)}%)
         </p>
   )}
            </div>
              )}

  {/* Universal Options Panel */}
     {!selectedRule?.hasBeenDistributed && (
    <UniversalOptionsPanel
      options={{
          enableMinimumFloor: formData.enableMinimumFloor,
   minimumFloorAmount: formData.minimumFloorAmount,
    enableHourFactor: formData.enableHourFactor,
    hourFactorWeight: formData.hourFactorWeight,
      enableSeniorityFactor: formData.enableSeniorityFactor,
          seniorityFactorWeight: formData.seniorityFactorWeight,
        seniorityBands: tenantSeniorityBands, // Use tenant-level bands
      }}
    onChange={handleUniversalOptionsChange}
        readonly={selectedRule?.hasBeenDistributed}
       onConfigureSeniority={() => setShowSeniorityModal(true)}
/>
         )}

   {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4 border-t border-border">
    <button type="button" onClick={handleCancelEdit} className="btn btn-outline">
           Cancel
      </button>
  <div className="flex gap-2">
      {selectedRule && !selectedRule.hasBeenDistributed && (
     <button
             type="button"
             onClick={handleSimulateRule}
        className="btn btn-outline"
          >
<Icon name="FlaskConical" size={16} />
    Test in Lab
 </button>
        )}
    {!selectedRule?.hasBeenDistributed && (
           <button
      type="button"
  onClick={handleSave}
              className="btn btn-primary"
      disabled={mutationLoading || (requiresManualAllocation && !isValidPercentage)}
          >
  {mutationLoading ? (
          <>
                   <div className="spinner spinner-sm" />
   Saving...
       </>
     ) : (
   <>
         <Icon name="Check" size={16} />
     {isCreating ? "Create Rule" : "Save Changes"}
             </>
  )}
      </button>
          )}
   </div>
        </div>
 </div>
       </div>
    </PageSection>
     ) : (
       <PageSection title="Rule Details">
            <div className="card p-8 text-center">
           <Icon name="MousePointerClick" size={48} className="mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-lg font-semibold mb-2">Select a Rule</h3>
            <p className="text-muted-foreground mb-4">
        Click on a rule from the list to view and edit its details, or create a new rule.
         </p>
    <button className="btn btn-primary" onClick={handleCreateNew}>
             <Icon name="Plus" size={18} />
    Create New Rule
             </button>
             </div>
</PageSection>
   )}
         </div>
    </div>
      )}

  {/* Rule Activation Modal */}
              {showActivationModal && ruleToActivate && (
           <RuleActivationModal
       rule={ruleToActivate}
            onClose={() => {
   setShowActivationModal(false);
        setRuleToActivate(null);
          }}
         onConfirm={handleActivateConfirm}
      />
    )}

       {/* Seniority Bands Modal - Tenant-level configuration */}
        {showSeniorityModal && (
            <SeniorityBandsModal
     isOpen={showSeniorityModal}
   bands={tenantSeniorityBands}
     onSave={handleSeniorityBandsSave}
        onClose={() => setShowSeniorityModal(false)}
        />
        )}
      </PageLayout>
        </>
    );
};

export default RuleManager;
